import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import * as XLSX from 'xlsx';
import ThemeContext from '../../ThemeContext';
import Pagination from '../../Utils/Pagination';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

axios.defaults.baseURL = `${process.env.REACT_APP_BACKEND_URL}`;

const EmailLogs = () => {
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const location = useLocation();

  // Typography from theme
  const fontFamily =
    theme.typography?.fontFamily ||
    '"Roboto", "Helvetica Neue", Arial, sans-serif';
  const bodyFontSize = theme.typography?.bodyFontSize || 14;
  const titleFontSize = theme.typography?.titleFontSize || 24;

  const [logs, setLogs] = useState([]);
  const [apiError, setApiError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [gotoPage, setGotoPage] = useState('');
  const [sortField, setSortField] = useState('sent_on');
  const [sortOrder, setSortOrder] = useState('desc');

  // Fetch logs
  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setApiError('No authentication token found. Please log in.');
          return;
        }
        const response = await axios.get('/api/webmanager/mass-mail/logs', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setLogs(response.data);
        setApiError(null);
      } catch (error) {
        console.error('Error fetching email logs:', error);
        setApiError(
          error.code === 'ERR_NETWORK'
            ? 'Cannot connect to the backend server.'
            : error.response?.status === 403
            ? 'Unauthorized access.'
            : error.response?.status === 404
            ? 'Logs endpoint not found.'
            : 'Failed to fetch email logs.'
        );
        toast.error('Failed to fetch email logs.');
      }
    };

    fetchLogs();

    const handleFocus = () => fetchLogs();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [location]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortValue = (log, field) => {
    if (field === 'sent_on') return new Date((log.sent_on || '') + 'Z');
    let val;
    switch (field) {
      case 'subject':
        val = log.subject || 'N/A';
        break;
      case 'sent_to':
        val = log.sent_to || 'N/A';
        break;
      case 'name':
        val = log.name || 'N/A';
        break;
      default:
        val = log[field] || 'N/A';
    }
    return typeof val === 'string' ? val.toLowerCase() : val;
  };

  const sortedLogs = logs.sort((a, b) => {
    const valA = getSortValue(a, sortField);
    const valB = getSortValue(b, sortField);
    let ascCmp;
    if (valA instanceof Date && valB instanceof Date) {
      ascCmp = valA.getTime() - valB.getTime();
    } else if (valA === null && valB === null) {
      ascCmp = 0;
    } else if (valA === null) {
      ascCmp = 1;
    } else if (valB === null) {
      ascCmp = -1;
    } else {
      ascCmp = valA < valB ? -1 : valA > valB ? 1 : 0;
    }
    return sortOrder === 'asc' ? ascCmp : -ascCmp;
  });

  const paginatedLogs = sortedLogs.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  const handleExportLogs = () => {
    if (!sortedLogs.length) {
      toast.info('No logs to export.');
      return;
    }

    const data = sortedLogs.map((log) => ({
      'Sent On': new Date((log.sent_on || '') + 'Z').toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      }),
      'Sent To': log.sent_to,
      Name: log.name || 'N/A',
      'Message Subject': log.subject || 'N/A',
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Email Logs');
    XLSX.writeFile(workbook, 'Notification_Logs.xlsx');
  };

  return (
    <div style={{ fontFamily, fontSize: `${bodyFontSize}px` }}>
      {/* BREADCRUMB - Outside container, top-left */}
      <div
        style={{
          marginBottom: '0.5rem',
          marginTop: '10px',
          fontSize: `${Math.max(11, bodyFontSize - 1)}px`,
          fontFamily,
          paddingLeft: '1rem',
        }}
      >
        <span style={{ color: theme.colors.primary }}>
          <Link
            to="/superadmin/notify"
            style={{
              color: theme.colors.primary,
              textDecoration: 'underline',
              fontFamily,
            }}
          >
            Notify Users
          </Link>
        </span>
        <span style={{ margin: '0 0.5rem', color: theme.colors.textPrimary }}>
          &gt;
        </span>
        <span style={{ color: theme.colors.textPrimary }}>Logs</span>
      </div>

      {/* WHITE CARD CONTAINER */}
      <div
        style={{
          backgroundColor: "#ffffff",
          padding: "1rem",
          borderRadius: "10px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          fontFamily,
          marginTop: "0",
          marginBottom: "20px",
        }}
      >
        {/* HEADER */}
        <div
          style={{
            display: 'flex',
            flexDirection: window.innerWidth < 600 ? 'column' : 'row',
            alignItems: window.innerWidth < 600 ? 'stretch' : 'center',
            justifyContent: 'space-between',
            marginBottom: '1rem',
            gap: '0.5rem',
          }}
        >
          <h4
            style={{
              fontSize: `${titleFontSize}px`,
              fontWeight: 'bold',
              color: theme.colors.textPrimary,
              marginBottom: 0,
              fontFamily,
            }}
          >
            Notification Logs
          </h4>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: window.innerWidth < 600 ? 'stretch' : 'flex-end',
            }}
          >
            <button
              onClick={handleExportLogs}
              style={{
                background: theme.gradients?.secondary || theme.colors.secondary,
                color: theme.colors.textWhite,
                padding: "6px 12px",
                borderRadius: "8px",
                fontSize: "clamp(10px, 2.5vw, 11px)",
                border: "none",
                minWidth: "120px",
                textAlign: "center",
                fontFamily,
              }}
            >
              Export Logs
            </button>
          </div>
        </div>

        {/* TOP PAGINATION */}
        <Pagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          perPage={perPage}
          setPerPage={setPerPage}
          totalItems={sortedLogs.length}
          gotoPage={gotoPage}
          setGotoPage={setGotoPage}
        />

        {/* TABLE CONTAINER */}
        <div
          style={{
            overflowX: "auto",
            backgroundColor: "#ffffff",
            borderRadius: "10px",
            border: "1px solid #dee2e6",
            marginTop: "1rem",
            marginBottom: "1rem",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: `${bodyFontSize}px`,
              fontFamily,
            }}
          >
            <thead>
              <tr style={{ backgroundColor: theme.colors.primary, color: theme.colors.textWhite }}>
                <th className="p-2" style={{ cursor: 'pointer', width: '25%' }} onClick={() => handleSort('sent_on')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    Sent On
                    {sortField === 'sent_on' && (
                      <i className={`bi ${sortOrder === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'} ms-1`}></i>
                    )}
                  </span>
                </th>
                <th className="p-2" style={{ cursor: 'pointer', width: '25%' }} onClick={() => handleSort('sent_to')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    Sent To
                    {sortField === 'sent_to' && (
                      <i className={`bi ${sortOrder === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'} ms-1`}></i>
                    )}
                  </span>
                </th>
                <th className="p-2" style={{ cursor: 'pointer', width: '25%' }} onClick={() => handleSort('name')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    Name
                    {sortField === 'name' && (
                      <i className={`bi ${sortOrder === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'} ms-1`}></i>
                    )}
                  </span>
                </th>
                <th className="p-2" style={{ cursor: 'pointer', width: '25%' }} onClick={() => handleSort('subject')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    Message Subject
                    {sortField === 'subject' && (
                      <i className={`bi ${sortOrder === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'} ms-1`}></i>
                    )}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedLogs.length === 0 ? (
                <tr>
                  <td
                    className="text-center p-3"
                    colSpan={4}
                    style={{ fontFamily }}
                  >
                    {apiError || 'No email logs found.'}
                  </td>
                </tr>
              ) : (
                paginatedLogs.map((log) => (
                  <tr
                    key={log.id}
                    style={{ borderBottom: "1px solid #dee2e6" }}
                  >
                    <td className="p-2">
                      {new Date((log.sent_on || '') + 'Z').toLocaleString('en-IN', {
                        timeZone: 'Asia/Kolkata',
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true,
                      })}
                    </td>
                    <td className="p-2">{log.sent_to}</td>
                    <td className="p-2">{log.name || 'N/A'}</td>
                    <td className="p-2">{log.subject || 'N/A'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* BOTTOM PAGINATION */}
        <Pagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          perPage={perPage}
          setPerPage={setPerPage}
          totalItems={sortedLogs.length}
          gotoPage={gotoPage}
          setGotoPage={setGotoPage}
        />
      </div>
    </div>
  );
};

export default EmailLogs;